import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Logo";
import { Users, Heart, MessageCircle, Send, Crown, Sparkles, Loader2, Lock, Flag,
  Search, Store, Plus, Globe, Phone, MapPin, Star } from "lucide-react";

const CATEGORIES = ["Restaurant & Food", "Beauty & Salon", "Health & Wellness", "Retail & Fashion",
  "Professional Services", "Legal & Finance", "Tech & Media", "Arts & Entertainment",
  "Education", "Home & Trades", "Nonprofit & Community", "Other"];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Avatar({ name }) {
  return (
    <span className="w-9 h-9 rounded-full bg-[#FF5A00] text-[#0a0a0f] font-black flex items-center justify-center shrink-0 font-display">
      {(name || "M")[0]?.toUpperCase()}
    </span>
  );
}

function Comments({ postId, canPost, onadded, onUpgrade }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/community/posts/${postId}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [postId]);

  const send = async (e) => {
    e.preventDefault();
    if (!canPost) { onUpgrade("Commenting is a Pro feature. Upgrade to join the conversation."); return; }
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/community/posts/${postId}/comments`, { content: text });
      setComments((p) => [...p, data]); setText(""); onadded && onadded();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not comment"); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#2a3040] space-y-3" data-testid={`comments-${postId}`}>
      {comments.map((c) => (
        <div key={c.id} className="flex gap-2.5">
          <Avatar name={c.author_name} />
          <div className="min-w-0">
            <div className="text-xs text-[#9ca3af]"><span className="text-white font-semibold">{c.author_name}</span> · {timeAgo(c.created_at)}</div>
            <p className="text-sm text-[#c7ccd6] whitespace-pre-wrap break-words">{c.content}</p>
          </div>
        </div>
      ))}
      <form onSubmit={send} className="flex gap-2">
        <input data-testid={`comment-input-${postId}`} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={canPost ? "Add a comment…" : "Upgrade to Pro to comment"}
          className="flex-1 bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00]" />
        <button type="submit" disabled={sending} data-testid={`comment-send-${postId}`}
          className="shrink-0 text-[#FF5A00] hover:text-[#FF7A33] p-2 disabled:opacity-50">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}

function BusinessDirectory({ isOwner }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category: CATEGORIES[0], city: "", description: "", website: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/community/businesses").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category.trim()) { toast.error("Name and category are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/community/businesses", form);
      setItems((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ name: "", category: CATEGORIES[0], city: "", description: "", website: "", phone: "" });
      setShowForm(false);
      toast.success("Business added to the directory");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not add business"); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    try { await api.delete(`/admin/community/businesses/${b.id}`); setItems((p) => p.filter((x) => x.id !== b.id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const feature = async (b) => {
    try {
      const { data } = await api.post(`/admin/community/businesses/${b.id}/feature`);
      setItems((p) => p.map((x) => ({ ...x, featured: x.id === b.id ? data.featured : false })));
      toast.success(data.featured ? "Featured for the week ⭐" : "Removed from featured");
    } catch { toast.error("Could not update"); }
  };

  const featured = items.find((b) => b.featured);
  const term = q.trim().toLowerCase();
  const filtered = items.filter((b) => {
    if (b.featured) return false;
    if (cat !== "All" && b.category !== cat) return false;
    if (term && ![b.name, b.category, b.city, b.description].join(" ").toLowerCase().includes(term)) return false;
    return true;
  });
  const inputCls = "w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00]";

  const Card = ({ b, feat }) => (
    <div className={`rounded-xl p-4 border ${feat ? "border-[#FF5A00]/50 bg-[#FF5A00]/[0.07]" : "border-[#2a3040] bg-[#15151d]"}`} data-testid={`biz-${b.id}`}>
      {feat && <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-1.5"><Star size={12} fill="#FF5A00" /> Featured this week</div>}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-white truncate">{b.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#FF5A00] font-bold">{b.category}</div>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid={`biz-feature-${b.id}`} onClick={() => feature(b)}
              className={`text-xs ${b.featured ? "text-[#FF5A00]" : "text-[#6b7280] hover:text-[#FF5A00]"}`}>
              {b.featured ? "Unfeature" : "Feature"}
            </button>
            <button data-testid={`biz-remove-${b.id}`} onClick={() => remove(b)} className="text-[#6b7280] hover:text-[#f87171] text-xs">Remove</button>
          </div>
        )}
      </div>
      {b.description && <p className="text-sm text-[#c7ccd6] mt-2 leading-relaxed">{b.description}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#9ca3af]">
        {b.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {b.city}</span>}
        {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-white"><Phone size={12} /> {b.phone}</a>}
        {b.website && <a href={b.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#FF5A00] hover:underline"><Globe size={12} /> Website</a>}
      </div>
    </div>
  );

  return (
    <div data-testid="business-directory">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <input data-testid="biz-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search businesses…" className={inputCls + " pl-9"} />
        </div>
        <select data-testid="biz-filter" value={cat} onChange={(e) => setCat(e.target.value)}
          className={inputCls + " w-auto"}>
          <option value="All">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button data-testid="biz-add-toggle" onClick={() => setShowForm((v) => !v)}
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-4 py-2.5 hover:bg-[#FF7A33] transition-colors">
          <Plus size={16} /> Add
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-5 bg-[#15151d] border border-[#2a3040] rounded-xl p-4 space-y-2.5" data-testid="biz-form">
          <div className="grid sm:grid-cols-2 gap-2.5">
            <input data-testid="biz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Business name *" className={inputCls} required />
            <select data-testid="biz-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} required>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="biz-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City / area" className={inputCls} />
            <input data-testid="biz-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className={inputCls} />
          </div>
          <input data-testid="biz-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" className={inputCls} />
          <textarea data-testid="biz-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What do they offer?" className={inputCls + " resize-y"} />
          <button type="submit" disabled={saving} data-testid="biz-submit"
            className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Store size={16} />} Add to directory
          </button>
        </form>
      )}

      {featured && cat === "All" && !term && (
        <div className="mb-4" data-testid="biz-featured"><Card b={featured} feat /></div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#FF5A00]" /></div>
      ) : (filtered.length === 0 && !(featured && cat === "All" && !term)) ? (
        <div className="text-center py-12 text-[#6b7280]" data-testid="biz-empty">
          <Store size={32} className="mx-auto mb-3 opacity-50" />
          {items.length === 0 ? "No businesses yet. Add the first one!" : "No matches for your filter."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="biz-list">
          {filtered.map((b) => <Card key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
}

export default function CommunityView({ onUpgrade }) {
  const { isPro, user } = useAuth();
  const isOwner = user?.role === "owner";
  const [tab, setTab] = useState("feed");
  const [posts, setPosts] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([api.get("/community/feed"), api.get("/community/prompt")])
      .then(([f, p]) => { setPosts(f.data); setPrompt(p.data.text || ""); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitPost = async () => {
    if (!isPro) { onUpgrade("Posting is a Pro feature. Upgrade to share with the community."); return; }
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/community/posts", { content: draft });
      setPosts((p) => [data, ...p]); setDraft("");
      toast.success("Posted to the community");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };

  const like = async (post) => {
    try {
      const { data } = await api.post(`/community/posts/${post.id}/like`);
      setPosts((p) => p.map((x) => x.id === post.id ? { ...x, liked: data.liked, like_count: data.like_count } : x));
    } catch { toast.error("Could not update"); }
  };

  const removePost = async (post) => {
    try {
      await api.delete(`/admin/community/posts/${post.id}`);
      setPosts((p) => p.filter((x) => x.id !== post.id));
      toast.success("Post removed");
    } catch { toast.error("Could not remove post"); }
  };

  const reportPost = async (post) => {
    if (!window.confirm("Report this post to the moderators?")) return;
    try {
      await api.post(`/community/posts/${post.id}/report`, { reason: "" });
      toast.success("Reported — thank you. Our team will review it.");
    } catch { toast.error("Could not send report"); }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-1">
          <Users size={22} className="text-[#FF5A00]" />
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight"><Brand /> Community</h1>
        </div>
        <p className="text-[#9ca3af] text-sm mb-6">Knowledge, wisdom, and voices from The Black Community.</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="community-tabs">
          <button data-testid="tab-feed" onClick={() => setTab("feed")}
            className={`inline-flex items-center gap-1.5 text-sm font-bold rounded-lg px-4 py-2 border transition-colors ${tab === "feed" ? "bg-[#FF5A00] text-[#0a0a0f] border-[#FF5A00]" : "bg-[#15151d] text-[#9ca3af] border-[#2a3040] hover:text-white"}`}>
            <Users size={15} /> Feed
          </button>
          <button data-testid="tab-biz" onClick={() => setTab("biz")}
            className={`inline-flex items-center gap-1.5 text-sm font-bold rounded-lg px-4 py-2 border transition-colors ${tab === "biz" ? "bg-[#FF5A00] text-[#0a0a0f] border-[#FF5A00]" : "bg-[#15151d] text-[#9ca3af] border-[#2a3040] hover:text-white"}`}>
            <Store size={15} /> Black Business Directory
          </button>
        </div>

        {tab === "biz" ? <BusinessDirectory isOwner={isOwner} /> : (
        <>

        {/* Weekly prompt */}
        {prompt && (
          <div className="mb-6 rounded-xl border border-[#FF5A00]/30 bg-[#FF5A00]/[0.06] p-4" data-testid="community-prompt">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-1">
              <Sparkles size={13} /> This week's prompt
            </div>
            <p className="text-white font-display font-bold text-lg leading-snug">{prompt}</p>
          </div>
        )}

        {/* Composer */}
        {isPro ? (
          <div className="mb-6 bg-[#15151d] border border-[#2a3040] rounded-xl p-4" data-testid="community-composer">
            <textarea data-testid="community-post-input" value={draft} onChange={(e) => setDraft(e.target.value)}
              rows={3} placeholder="Share knowledge, a win, or a question with the community…"
              className="w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] resize-y" />
            <div className="flex justify-end mt-2">
              <button data-testid="community-post-button" onClick={submitPost} disabled={posting}
                className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
                {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Post
              </button>
            </div>
          </div>
        ) : (
          <button data-testid="community-upgrade-cta" onClick={() => onUpgrade("Posting is a Pro feature. Upgrade to share with the community.")}
            className="mb-6 w-full flex items-center justify-center gap-2 bg-[#FF5A00]/10 border border-[#FF5A00]/30 text-[#FF5A00] font-bold rounded-xl px-4 py-3 hover:bg-[#FF5A00]/20 transition-colors">
            <Lock size={16} /> Upgrade to Pro to post &amp; comment
          </button>
        )}

        {/* Feed */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#FF5A00]" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-[#6b7280]" data-testid="community-empty">
            <Users size={32} className="mx-auto mb-3 opacity-50" />
            No posts yet. {isPro ? "Be the first to share!" : "Check back soon."}
          </div>
        ) : (
          <div className="space-y-4" data-testid="community-feed">
            {posts.map((post, i) => (
              <div key={post.id}>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }} data-testid={`community-post-${post.id}`}
                className="bg-[#15151d] border border-[#2a3040] rounded-xl p-4">
                <div className="flex items-center gap-2.5 mb-2">
                  <Avatar name={post.author_name} />
                  <div>
                    <div className="text-sm text-white font-semibold flex items-center gap-1.5">
                      {post.author_name}
                      {post.is_pro_author && <Crown size={12} className="text-[#FF5A00]" />}
                    </div>
                    <div className="text-[11px] text-[#6b7280]">{timeAgo(post.created_at)}</div>
                  </div>
                  {isOwner && (
                    <button data-testid={`community-delete-${post.id}`} onClick={() => removePost(post)}
                      className="ml-auto text-[#6b7280] hover:text-[#f87171] p-1 text-xs" title="Remove post">
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-[15px] text-[#e5e7eb] whitespace-pre-wrap break-words leading-relaxed">{post.content}</p>
                <div className="flex items-center gap-5 mt-3 text-sm">
                  <button data-testid={`like-${post.id}`} onClick={() => like(post)}
                    className={`inline-flex items-center gap-1.5 transition-colors ${post.liked ? "text-[#FF5A00]" : "text-[#9ca3af] hover:text-white"}`}>
                    <Heart size={16} fill={post.liked ? "#FF5A00" : "none"} /> {post.like_count > 0 ? post.like_count : ""}
                  </button>
                  <button data-testid={`comment-toggle-${post.id}`} onClick={() => setOpenComments(openComments === post.id ? null : post.id)}
                    className="inline-flex items-center gap-1.5 text-[#9ca3af] hover:text-white transition-colors">
                    <MessageCircle size={16} /> {post.comment_count > 0 ? post.comment_count : ""} Comment
                  </button>
                  <button data-testid={`report-${post.id}`} onClick={() => reportPost(post)}
                    className="inline-flex items-center gap-1.5 text-[#6b7280] hover:text-[#f87171] transition-colors ml-auto">
                    <Flag size={15} /> Report
                  </button>
                </div>
                {openComments === post.id && (
                  <Comments postId={post.id} canPost={isPro} onUpgrade={onUpgrade}
                    onadded={() => setPosts((p) => p.map((x) => x.id === post.id ? { ...x, comment_count: (x.comment_count || 0) + 1 } : x))} />
                )}
              </motion.div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
